/**
 * Login Page
 */

import { LocationContext } from '@/shared/types/models'
import { EmailPhoneToggle, InputWithIcon } from '@shared/components/form'
import { PhoneInput } from '@shared/components/forms'
import { Button } from '@shared/components/ui/button'
import { Card } from '@shared/components/ui/card'
import { Checkbox } from '@shared/components/ui/checkbox'
import { useAuth } from '@shared/contexts'
import { actionStorage, credentialsStorage } from '@shared/lib/storage'
import { LogIn, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location: LocationContext = useLocation()
  const [isPhone, setIsPhone] = useState(true)
  const [credential, setCredential] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true) // Default to checked
  const [isLoading, setIsLoading] = useState(false)
  const [savedCredentials, setSavedCredentials] = useState<{ type: 'email' | 'phone'; value: string } | null>(null)

  // Load saved credentials on mount
  useEffect(() => {
    const saved = credentialsStorage.getCredentials()
    setSavedCredentials(saved)
    if (saved) {
      setCredential(saved.value)
      setIsPhone(saved.type === 'phone')
    }
  }, [])

  // Handle toggle: clear field if toggling away from saved type, restore if toggling back
  const handleToggle = (newIsPhone: boolean) => {
    setIsPhone(newIsPhone)

    // If toggling back to the saved credential type, restore it
    if (savedCredentials && newIsPhone === (savedCredentials.type === 'phone')) {
      setCredential(savedCredentials.value)
    } else {
      // Otherwise clear the field
      setCredential('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Clean phone number if it's not an email
      let cleanedCredential = credential.includes('@') ? credential : credential.replace(/\D/g, '')

      // Add Argentina prefix (54 9) if it's a phone number (10 digits without country code)
      if (!credential.includes('@') && cleanedCredential.length === 10) {
        cleanedCredential = '549' + cleanedCredential
      }

      await login({ email: cleanedCredential, password, remember: rememberMe })
      toast.success('¡Sesión iniciada correctamente!')

      // Save or clear credentials based on remember me
      if (rememberMe) {
        // Extract unformatted value for storage
        const storageValue = isPhone
          ? credential.replace(/\D/g, '') // Remove all non-digits for phone
          : credential // Email is already good

        credentialsStorage.setCredentials({
          type: isPhone ? 'phone' : 'email',
          value: storageValue, // Store unformatted
        })
      } else {
        credentialsStorage.clearCredentials()
      }

      // Check for pending action
      const pendingAction = actionStorage.getPendingAction()

      if (pendingAction) {
        // Navigate to return path
        void navigate(pendingAction.returnPath, { replace: true })
      } else if (location.state?.from) {
        // Use stored location from navigation state
        void navigate(location.state.from, { replace: true })
      } else {
        // If user was already authenticated, go back where they came from
        // Otherwise go to home
        void navigate(location.state?.from || '/', { replace: true })
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al iniciar sesión')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGuestAccess = () => {
    void navigate('/')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-auth bg-cover bg-center p-4">
      <Card className="w-full max-w-md p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-center mb-6">Iniciar Sesión</h1>

        {/* Email/Phone Toggle */}
        <EmailPhoneToggle
          isPhone={isPhone}
          onToggle={handleToggle}
          disabled={true}
        />

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 mt-6">
          {/* Email or Phone Input */}
          {isPhone ? (
            <PhoneInput
              value={credential}
              onChange={(value) => setCredential(value)}
              required
            />
          ) : (
            <InputWithIcon
              icon={Mail}
              type="email"
              placeholder="Correo electrónico"
              value={credential}
              onChange={(e) => setCredential(e.target.value)}
              required
            />
          )}

          {/* Password Input with Toggle */}
          <InputWithIcon
            isPasswordToggle
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {/* Remember Me Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(!!checked)}
            />
            <label
              htmlFor="remember"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Recordarme
            </label>
          </div>

          {/* Submit Button */}
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                Iniciar Sesión
              </>
            )}
          </Button>

          {/* Guest Access Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGuestAccess}
          >
            Continuar sin iniciar sesión
          </Button>

          {/* Password Recovery Link */}
          <div className="text-center">
            <Link
              to="/passwordrecovery"
              className="text-sm text-primary hover:underline"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          {/* Register Link */}
          <div className="text-center text-sm text-muted-foreground">
            ¿No tienes cuenta?
          </div>
          <Button variant="outline" className="w-full" asChild>
            <Link to="/register">
              Regístrate
            </Link>
          </Button>
        </form>
      </Card>
    </div>
  )
}
